import ReturnHeaderMeta from "@dana/Components/StandardReturns/ReturnHeaderMeta";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontWeights } from "@dana/MobiusOverrides/styles/typography";
import { ReturnOrderLineModel } from "@dana/Services/DanaStandardReturnOrderService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import Checkbox from "@insite/mobius/Checkbox";
import DataTable from "@insite/mobius/DataTable";
import {
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Pagination,
    Tooltip,
} from "@insite/mobius/index";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// --- Props Interface ---
interface StepSelectItemsProps {
    onChangeQuery: (q: string) => void;
    onToggleReturn: (lineNo: string) => void;
    onSetReturnQty: (lineNo: string, qty: number) => void;
}

// --- Styles Interfaces and Objects ---
export interface StepSelectItemsStyles {
    modal?: ModalPresentationProps;
    tableWrap?: InjectableCss;
    headerMetaWrapper?: InjectableCss;
    disabledRow?: InjectableCss;
    tooltipText?: TooltipPresentationProps;
}

export const StepSelectItemsStyles: StepSelectItemsStyles = {
    modal: {
        size: 1140,
        sizeVariant: "large",
    },
    tableWrap: {
        css: css`
            overflow: auto;
        `,
    },
    disabledRow: {
        css: css`
            td:not(.tooltip-cell) {
                opacity: 0.55;
                cursor: not-allowed;
            }
        `,
    },
    tooltipText: {
        mergeCss: true,
        cssOverrides: {
            tooltipBody: css`
                font-weight: ${fontWeights.regular};
            `,
            tooltipContainer: css`
                width: auto;
                max-width: unset;
                top: 50%;
                transform: translateY(-50%);
                right: 22px;
                left: unset;
                bottom: unset;
            `,
            tooltipArrow: css`
                right: -12px;
                left: unset;
                top: 50%;
                transform: translateY(-50%) rotate(-90deg);
            `,
        },
    },
    headerMetaWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 32px;
            margin-bottom: 16px;

            @media only screen and (max-width: 767px) {
                flex-direction: column;
                align-items: flex-start;
            }

            .order-no-wrapper {
                padding: 12px;
                border: 2px dashed ${themeColors.primary.richBlack};
                border-radius: 4px;
                display: flex;
                align-items: center;
                gap: 16px;

                @media only screen and (max-width: 767px) {
                    flex-direction: column;
                    align-items: flex-start;
                }

                .order-no-box {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .meta-title {
                        color: ${themeBackgrounds.darkGray};
                    }
                }
            }

            .search-wrapper {
                flex-grow: 1;

                @media only screen and (max-width: 767px) {
                    width: 100%;
                }
            }
        `,
    },
};

const styles = StepSelectItemsStyles;
// --- Component Definition ---
const mapStateToProps = (state: ApplicationStateDana) => {
    return {
        lines: state.standardReturnState?.selectedLines,
        filteredQuery: state.standardReturnState?.filteredQuery,
    };
};
type Props = ReturnType<typeof mapStateToProps> & StepSelectItemsProps;
// --- Component Definition ---
const StepSelectItems: FC<Props> = ({ lines, filteredQuery, onChangeQuery, onToggleReturn, onSetReturnQty }) => {
    const filteredLines = useMemo(() => {
        const q = filteredQuery?.trim().toLowerCase();
        if (!q) {
            return lines;
        }
        return lines?.filter(
            l =>
                l?.lineNumber.toString().toLowerCase().includes(q) ||
                l?.productErpNumber.toLowerCase().includes(q) ||
                l?.description.toLowerCase().includes(q),
        );
    }, [filteredQuery, lines]);

    const [pagination, setPagination] = useState<PaginationModel | undefined>({
        currentPage: 1,
        pageSize: 12,
        totalItemCount: filteredLines?.length,
        pageSizeOptions: [12, 24, 36, 48],
    } as PaginationModel);

    const [currentItems, setCurrentItems] = useState<ReturnOrderLineModel[]>(filteredLines);
    useEffect(() => {
        paginateData();
    }, [pagination?.currentPage, pagination?.pageSize, filteredLines]);

    const paginateData = () => {
        if (pagination) {
            const startIndex = (pagination?.currentPage - 1) * pagination?.pageSize;
            const endIndex = startIndex + pagination?.pageSize;
            setCurrentItems(filteredLines?.slice(startIndex, endIndex));
        }
    };

    const onChangePage = (page: number) => {
        setPagination((prev: any) => ({
            ...prev,
            currentPage: page,
        }));
    };

    const onChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (event.target.value) {
            setPagination((prev: any) => ({
                ...prev,
                pageSize: event.target.value,
                currentPage: 1, // reset to first page
            }));
        }
    };

    const disabledProps = styles.disabledRow;

    const L_LINE = translate("Line No");
    const L_PART = translate("Part No.");
    const L_CPART = translate("Customer Part No.");
    const L_DESC = translate("Description");
    const L_OQTY = translate("Shipped Qty");
    const L_CQTY = translate("Return Item Qty");
    const L_CANCEL = translate("Return");
    return (
        <StyledWrapper>
            <StyledWrapper {...styles.headerMetaWrapper}>
                {/* Order Number Header */}
                <ReturnHeaderMeta />
                {/* Search Section */}
                <StyledWrapper className="search-wrapper">
                    <TextField
                        type="search"
                        value={filteredQuery}
                        onChange={e => onChangeQuery(e.target.value)}
                        placeholder={translate("Search by Part #, Customer Part #, Description, or Line #")}
                        aria-label={translate("Search order lines")}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.tableWrap}>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader tight>{L_LINE}</DataTableHeader>
                        <DataTableHeader>{L_PART}</DataTableHeader>
                        <DataTableHeader>{L_CPART}</DataTableHeader>
                        <DataTableHeader>{L_DESC}</DataTableHeader>
                        <DataTableHeader>{L_OQTY}</DataTableHeader>
                        <DataTableHeader>{L_CQTY}</DataTableHeader>
                        <DataTableHeader>{L_CANCEL}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {currentItems?.map(line => {
                            const checkBoxDisabled = line.qtyShipped <= 0 || !line.isReturnable;
                            const qtyDisabled = !line.return || line.qtyShipped <= 0 || !line.isReturnable;
                            const invalid =
                                line.return && (line.returnItemQty < 1 || line.returnItemQty > line.qtyShipped);
                            return (
                                <DataTableRow key={line.lineNumber} {...(checkBoxDisabled ? disabledProps : {})}>
                                    <DataTableCell>{line.lineNumber}</DataTableCell>
                                    <DataTableCell>{line.productErpNumber}</DataTableCell>
                                    <DataTableCell>{line.customerProductNumber}</DataTableCell>
                                    <DataTableCell>{line.description}</DataTableCell>
                                    <DataTableCell>{line.qtyShipped}</DataTableCell>
                                    <DataTableCell tight>
                                        <TextField
                                            type="number"
                                            max={line.qtyShipped}
                                            disabled={qtyDisabled}
                                            value={line.returnItemQty || 0}
                                            onBlur={e =>
                                                onSetReturnQty(
                                                    line.lineNumber?.toString(),
                                                    e.target.value ? parseInt(e.target.value, 10) : 0,
                                                )
                                            }
                                            onChange={e =>
                                                onSetReturnQty(
                                                    line.lineNumber?.toString(),
                                                    e.target.value ? parseInt(e.target.value, 10) : 0,
                                                )
                                            }
                                            error={invalid || !line.isReturnable}
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                        />
                                    </DataTableCell>
                                    <DataTableCell alignX="center" className="tooltip-cell">
                                        {!checkBoxDisabled ? (
                                            <Checkbox
                                                disabled={checkBoxDisabled}
                                                checked={!!line.return}
                                                onChange={() => onToggleReturn(line.lineNumber?.toString())}
                                                aria-label={`Mark line ${line.lineNumber?.toString()} for return`}
                                            ></Checkbox>
                                        ) : (
                                            <Tooltip
                                                {...styles.tooltipText}
                                                text={translate("Not eligible for return")}
                                            />
                                        )}
                                    </DataTableCell>
                                </DataTableRow>
                            );
                        })}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            {currentItems && currentItems?.length > 0 && pagination && (
                <Pagination
                    currentPage={pagination?.currentPage}
                    resultsPerPage={pagination?.pageSize}
                    resultsCount={pagination?.totalItemCount}
                    resultsPerPageOptions={pagination?.pageSizeOptions}
                    onChangePage={onChangePage}
                    resultsPerPageLabel="Display"
                    onChangeResultsPerPage={onChangeResultsPerPage}
                    prevPage={pagination?.prevPageUri}
                    nextPage={pagination?.nextPageUri}
                />
            )}
        </StyledWrapper>
    );
};
export default connect(mapStateToProps)(StepSelectItems);
